import React, { useState } from 'react';
import { useStorage } from '../../hooks/useStorage';
import { useLanguage } from '../../contexts/LanguageContext';
import { ProjectDocument } from '../../types/project';
import { TaskDocument } from '../../types/task';
import { 
  DocumentArrowUpIcon, 
  DocumentIcon, 
  PaperClipIcon, 
  XMarkIcon,
  EyeIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { Timestamp, serverTimestamp } from 'firebase/firestore';

export type DocumentUploadDocument = ProjectDocument | TaskDocument;

interface DocumentUploadProps {
  documents: DocumentUploadDocument[];
  onDocumentsChange: (documents: DocumentUploadDocument[]) => void;
  uploadPath: string; // pl. "projects/project-id/documents" vagy "tasks/task-id/documents"
  title?: string;
  subtitle?: string;
  maxFiles?: number;
  disabled?: boolean;
  acceptedTypes?: string[];
  maxFileSize?: number; // bytes
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  documents,
  onDocumentsChange,
  uploadPath,
  title = 'Dokumentumok',
  subtitle = 'Csatolja a projekthez/feladathoz kapcsolódó dokumentumokat',
  maxFiles = 10,
  disabled = false,
  acceptedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif'],
  maxFileSize = 10 * 1024 * 1024 // 10MB
}) => {
  const { t } = useLanguage();
  const { uploadFile, deleteFile } = useStorage();
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Fájl feltöltés kezelése
  const handleFileUpload = async (files: FileList) => {
    if (disabled) return;
    
    const fileArray = Array.from(files);
    
    // Validáció
    const validFiles = fileArray.filter(file => {
      if (file.size > maxFileSize) {
        alert(`${file.name} túl nagy (max ${Math.round(maxFileSize / (1024 * 1024))}MB)`);
        return false;
      }
      
      if (!acceptedTypes.includes(file.type)) {
        alert(`${file.name} formátuma nem támogatott`);
        return false;
      }
      
      return true;
    });

    if (validFiles.length === 0) return;

    // Ellenőrizzük, hogy nem lépjük-e túl a maximális fájlszámot
    if (documents.length + validFiles.length > maxFiles) {
      alert(`Maximum ${maxFiles} fájl tölthető fel`);
      return;
    }

    setIsUploading(true);

    try {
      const uploadPromises = validFiles.map(async (file) => {
        const timestamp = Date.now();
        const uniqueFileName = `${timestamp}_${file.name}`;
        const fullPath = `${uploadPath}/${uniqueFileName}`;

        const fileUrl = await uploadFile(file, fullPath);

        const newDocument: DocumentUploadDocument = {
          id: timestamp.toString(),
          name: uniqueFileName,
          originalName: file.name,
          url: fileUrl,
          uploadedAt: new Date(timestamp),
          type: 'other',
          size: file.size
        };

        return newDocument;
      });

      const newDocuments = await Promise.all(uploadPromises);
      onDocumentsChange([...documents, ...newDocuments]);
      
    } catch (error) {
      console.error('Hiba a fájl feltöltése során:', error);
      alert('Hiba történt a fájl feltöltése során');
    } finally {
      setIsUploading(false);
    }
  };

  // Dokumentum törlése
  const handleDocumentDelete = async (documentId: string) => {
    const documentToDelete = documents.find(doc => doc.id === documentId);
    if (!documentToDelete) return;

    try {
      // Frissítsük a documents listát
      const updatedDocuments = documents.filter(doc => doc.id !== documentId);
      onDocumentsChange(updatedDocuments);

      // Próbáljuk meg törölni a fájlt a storage-ból
      try {
        const fullPath = `${uploadPath}/${documentToDelete.name}`;
        await deleteFile(fullPath);
      } catch (error) {
        console.error('Hiba a fájl törlése során:', error);
        // Nem kritikus hiba, a document már el van távolítva a listából
      }
    } catch (error) {
      console.error('Hiba a dokumentum törlése során:', error);
      alert('Hiba történt a dokumentum törlése során');
    }
  };

  // Drag & Drop események
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  // Fájl méret formázás
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Fájl ikon meghatározása
  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '🖼️';
      default:
        return '📎';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="flex">
        <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
          <DocumentArrowUpIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div className="p-4 flex-1">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">{title}</h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {documents.length} / {maxFiles}
            </span>
          </div>

          <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">{subtitle}</p>

          {/* Drag & Drop feltöltési terület */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
              dragActive
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <PaperClipIcon className="mx-auto h-6 w-6 text-gray-400 dark:text-gray-500 mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Húzza ide a fájlokat vagy{' '}
              <label className={`cursor-pointer text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium ${
                disabled ? 'cursor-not-allowed' : ''
              }`}>
                böngészés
                <input
                  type="file"
                  multiple
                  accept={acceptedTypes.join(',')}
                  onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                  disabled={disabled || isUploading}
                  className="hidden"
                />
              </label>
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              PDF, Word, Kép (max {Math.round(maxFileSize / (1024 * 1024))}MB)
            </p>
          </div>

          {isUploading && (
            <div className="mt-4 text-center">
              <div className="inline-flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                {t('common.uploading') || 'Feltöltés...'}
              </div>
            </div>
          )}

          {/* Dokumentumok listája */}
          {documents.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Feltöltött dokumentumok:</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <span className="text-lg flex-shrink-0">
                        {getFileIcon(doc.originalName || doc.name)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {doc.originalName || doc.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {doc.size ? formatFileSize(doc.size) : '-'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <button
                        onClick={() => window.open(doc.url, '_blank')}
                        className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded"
                        title="Megtekintés"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <a
                        href={doc.url}
                        download={doc.originalName || doc.name}
                        className="p-1 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 rounded"
                        title="Letöltés"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                      </a>
                      <button
                        onClick={() => handleDocumentDelete(doc.id)}
                        className="p-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 rounded"
                        title="Törlés"
                        disabled={disabled}
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload; 